import { createAction, createSlice } from '@reduxjs/toolkit';
import { Reducers } from '@sda/enums/store';
import { getDateTime } from '@sda/utils/date';
import { extractSharedActions } from '@sda/utils/store';
import { createEntityAdapter } from '@client/store/entity.adapter';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AccountHistoryTypes } from '@sda/enums/hyperwallet';
import type { CreateHyperwalletPayload } from '@sda/functions/hyperwallet/post-create-user.endpoint';
import type { ProfileTypes } from '@sda/enums';
import type { UserAccountDto } from '@sda/functions/hyperwallet/post-update-user-account.endpoint';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type { DestinationAccount, HyperwalletAccount } from '@sda/interfaces/user/hyperwallet';

// LISTENERS

export const startAddHyperwalletAccountHistory = createAction<{
  accountId: string;
  historyType: AccountHistoryTypes;
  redirect: string;
}>(`${Reducers.HYPERWALLET_ACCOUNTS}/startAddHyperwalletAccountHistory`);

export const startDeactivateTransferMethod = createAction<string>(`${Reducers.HYPERWALLET_ACCOUNTS}/startDeactivateTransferMethod`);

export const startFetchHyperwalletAccount = createAction<string>(`${Reducers.HYPERWALLET_ACCOUNTS}/startFetchHyperwalletAccount`);
export const startRenameHyperwalletAccount = createAction<{ id: string, displayName: string }>(`${Reducers.HYPERWALLET_ACCOUNTS}/startRenameHyperwalletAccount`);

export const startSaveHyperwalletAccount = createAction<CreateHyperwalletPayload>(`${Reducers.HYPERWALLET_ACCOUNTS}/startSaveHyperwalletAccount`);

export const startSetDefaultHyperwalletAccount = createAction<{
  profileId: string; profileType: ProfileTypes; accountId: string;
}>(`${Reducers.HYPERWALLET_ACCOUNTS}/startSetDefaultHyperwalletAccount`);

export const startStoreHyperwalletAccountToken = createAction<UserAccountDto>(`${Reducers.HYPERWALLET_ACCOUNTS}/startStoreHyperwalletAccountToken`);

export const startTransferHyperwalletAccount = createAction<{
  startUserId: string; destinationUserId: string; hyperwalletAccountId: string;
}>(`${Reducers.HYPERWALLET_ACCOUNTS}/startTransferHyperwalletAccount`);

type TokenResponseCallback = (token: string) => void;

export const startFetchHyperwalletToken = createAction<{ userToken: string, onSuccess: TokenResponseCallback, onError: TokenResponseCallback }>(`${Reducers.HYPERWALLET_ACCOUNTS}/startFetchHyperwalletToken`);

// REDUCER

export const hyperwalletAccountAdapter = createEntityAdapter<HyperwalletAccount & FetchDataType>({
  sortComparer: (a, b) => getDateTime(b.createdAt).toMillis() - getDateTime(a.createdAt).toMillis(),
});

interface HyperwalletAccountState {
  destinationAccount: DestinationAccount;
}

const hyperwalletAccountsSlice = createSlice({
  initialState: hyperwalletAccountAdapter.getInitialState<HyperwalletAccountState>({ destinationAccount: null }),
  name: Reducers.HYPERWALLET_ACCOUNTS,
  reducers: {
    ...extractSharedActions(hyperwalletAccountAdapter),
    setDestinationAccount: (state, action: PayloadAction<DestinationAccount>) => {
      state.destinationAccount = action.payload;
    },
  },
});

export const {
  removeOne: removeHyperwalletAccount, addMany: addHyperwalletAccounts, setDestinationAccount, upsertMany: upsertHyperwalletAccounts,
} = hyperwalletAccountsSlice.actions;

export default hyperwalletAccountsSlice.reducer;
